import React, { useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import { LoadingIcon } from '@pkg/components';

import { type RedirectUrlItems, useCallMetaProxy } from '@app/helpers';

import {
    BodyText,
    Container,
    ContentContainer,
    ImageBg,
    InfoPill,
    TextContainer,
    UrlText,
} from './PagePreview.style.ts';

interface PagePreviewProps {
    redirectUrl: RedirectUrlItems;
}

export function PagePreview({ redirectUrl }: PagePreviewProps) {
    const [translate] = useTranslation(['power_ups', 'common']);

    const { data, isFetching, isLoading } = useCallMetaProxy({
        url: redirectUrl?.displayUrl?.length > 0 ? redirectUrl.url : '',
    });

    const metaTags = useMemo(() => {
        return Array.isArray(data) ? data : [];
    }, [data]);

    const placeholderImageSrc = '/assets/img/meta-placeholder@2x.jpg';
    const showLoader = isFetching || isLoading;
    const loadedUrl = metaTags.length ? redirectUrl.url : null;

    const metaImageSrc = useMemo(() => {
        if (metaTags?.length) {
            const image = metaTags.find((tag) => {
                return tag.property === 'og:image';
            });

            if (image && image.content?.length > 0) {
                return image.content;
            }
        }

        return placeholderImageSrc;
    }, [metaTags]);

    const showNoThumbPill = metaImageSrc === placeholderImageSrc;

    const previewMarkup =
        !showLoader && loadedUrl ? (
            <ContentContainer>
                <ImageBg $image={metaImageSrc} />
                {showNoThumbPill ? <InfoPill>{translate('no_thumb')}</InfoPill> : null}
                <TextContainer>
                    <UrlText>{loadedUrl}</UrlText>
                </TextContainer>
            </ContentContainer>
        ) : null;

    const loadingMarkup = showLoader ? (
        <ContentContainer>
            <LoadingIcon />
            {translate('hang_tight')}
        </ContentContainer>
    ) : null;

    const emptyMarkup =
        !showLoader && !loadedUrl ? (
            <ContentContainer $isEmptyImg>
                <ImageBg $image="/assets/img/empty@2x.png" $isEmptyImg />
                <TextContainer>
                    <UrlText>{translate('looks_empty')}</UrlText>
                    <BodyText>{translate('add_url_copy')}</BodyText>
                </TextContainer>
            </ContentContainer>
        ) : null;

    return (
        <Container>
            {loadingMarkup}
            {emptyMarkup}
            {previewMarkup}
        </Container>
    );
}
